import { Box, Chip, IconButton, Tooltip, Typography } from '@mui/material';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import { useTranslation } from 'react-i18next';
import { useMaterialUIController } from 'context';
import { getCrmColors } from '@layouts/pages/CRM/kanban-board/crm-theme';
import { formatCrmDateTime } from '@layouts/pages/CRM/kanban-board/formatters/crm-datetime';
import { ApiToken } from '../services/api-token.service';
import { scopeLabelKey } from '../api-token-scopes';
import { listCardSx, monoLineSx, statusPillSx, tagChipSx } from './integration-list.styles';

interface Props {
    token: ApiToken;
    onRevoke: () => void;
}

/// Uma chave gerada: o que ela pode, quando foi usada e o botão de revogar.
export const ApiTokenRow = ({ token, onRevoke }: Props) => {
    const { t } = useTranslation();
    const [controller] = useMaterialUIController();
    const cc = getCrmColors(controller.darkMode);

    const revoked = Boolean(token.revokedAt);

    return (
        <Box sx={listCardSx(cc, !revoked)}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: cc.textPrimary }} noWrap>
                    {token.name}
                </Typography>

                <Typography sx={monoLineSx(cc)}>
                    {token.prefix}…
                    {' · '}
                    {t('apiTokens.criadaEm', { quando: formatCrmDateTime(token.createdAt) })}
                </Typography>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                    {token.fullAccess ? (
                        <Tooltip title={t('apiTokens.acessoTotalAjuda')}>
                            <Chip
                                size="small"
                                label={t('apiTokens.acessoTotal')}
                                sx={tagChipSx(cc, cc.eventWarning)}
                            />
                        </Tooltip>
                    ) : (
                        token.scopes.map((scope) => {
                            const labelKey = scopeLabelKey(scope);

                            return (
                                <Chip
                                    key={scope}
                                    size="small"
                                    label={labelKey ? t(labelKey) : scope}
                                    sx={tagChipSx(cc)}
                                />
                            );
                        })
                    )}
                </Box>
            </Box>

            <Box sx={statusPillSx(cc, revoked || !token.lastUsedAt ? cc.textMuted : cc.eventSuccess)}>
                {revoked
                    ? t('apiTokens.revogada')
                    : token.lastUsedAt
                        ? t('apiTokens.usadaEm', { quando: formatCrmDateTime(token.lastUsedAt) })
                        : t('apiTokens.nuncaUsada')}
            </Box>

            {/* Revogada não volta: no lugar do botão fica um vão do mesmo tamanho, para as pílulas alinharem. */}
            <Box sx={{ width: 34, flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
                {!revoked && (
                    <Tooltip title={t('apiTokens.revogar')}>
                        <IconButton size="small" onClick={onRevoke} sx={{ color: cc.eventDanger }}>
                            <BlockOutlinedIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>
        </Box>
    );
};
